import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  FormLabel,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select, { MultiValue } from 'react-select';
import { format } from 'date-fns';
import {
  InputCheck,
  InputRange,
  PaginationComponent,
  SearchFilterDropdown,
  SectionHeader,
} from '../../common/components';
import {
  clearSearchResults,
  resetFilters,
  searchBookableAccommodations,
  selectBookableAccommodationsPagination,
  selectLoadingStatus,
  selectSearchFilter,
  selectSearchResults,
  setSearchFilters,
} from './searchBookableAccommodationsSlice';
import BasicSearchFilters from './components/BasicSearchFilters';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AccommodationAttribute,
  fetchAttributes,
  selectDistanceAttributes,
  selectEquipmentAttributes,
} from './attributesSlice';
import {
  addAccommodationToBookingContainerAsync,
  AddAccommodationToBookingContainerItem,
  BookingContainer,
  BookingDetailsOrderItem,
  selectBasketBookingContainer,
  setBasketContainer,
} from '../bookings/bookingsSlice';
import { getSeasonRatesAsync } from '../seasonRates/seasonRatesSlice';
import { AutoSuggestOption } from '../autocomplete/autocompleteSlice';
import { LOADING_STATUS, ORDER_DIRECTION, ROLES } from '../../common/constants';
import AccommodationAutoCompleteSearchBar from '../autocomplete/AccommodationAutoCompleteSearchBar';
import { dateToDateOnlyString } from '../../common/utils/dateUtils';
import { InitialPaginationParameters } from '../../common/types/pagination.types';
import { checkRole } from '../auth/authSlice';
import { useLocale } from '../../locales/useLocale';
import AgentAccommodationView from '../accommodations/AgentAccommodationView';
import {
  getAccommodationDetailsBasicAsync,
  selectAccommodationDetailsBasic,
} from '../accommodations/accommodationsSlice';
import { makeSportumTempBookingAsync } from '../accommodations/CapacityCalendar/CapacityCalendarSlice';

function SearchBookableAccommodations() {
  const { t } = useTranslation(); // Add this hook
  const dispatch = useAppDispatch();
  const history = useNavigate();
  const location = useLocation();
  const { currentLocale } = useLocale();
  const equipmentAttributes = useAppSelector(selectEquipmentAttributes);
  const distanceAttributes = useAppSelector(selectDistanceAttributes);
  const basketContainer = useAppSelector(selectBasketBookingContainer);
  const searchResultItems = useAppSelector(selectSearchResults);
  const searchFilter = useAppSelector(selectSearchFilter);
  const loadingStatus = useAppSelector(selectLoadingStatus);
  const searchResultItemsPagination = useAppSelector(
    selectBookableAccommodationsPagination,
  );
  const isTravelAgencyOrDomainUser = useAppSelector(
    checkRole([ROLES.TravelAgency, ROLES.TravelAgencyDomainUser]),
  );
  const isTravelAgencyUser = useAppSelector(checkRole([ROLES.TravelAgency]));
  const urlParams = new URLSearchParams(location.search);
  const initialSelectedDates = [
    urlParams.get('startDate') ? new Date(urlParams.get('startDate')) : null,
    urlParams.get('endDate') ? new Date(urlParams.get('endDate')) : null,
  ];

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [addingToBasket, setAddingToBasket] = useState<Set<string>>(new Set());

  const clearAddingToBasket = (id: string) =>
    setAddingToBasket(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  const [selectedDates, setSelectedDates] = useState(initialSelectedDates);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [freeTextUpdated, setFreeTextUpdated] = useState<boolean>(false);
  const [isAgentAccommodationModalOpen, setIsAgentAccommodationModalOpen] =
    useState<boolean>(true);
  const [agentViewableAccommodation, setAgentViewableAccommodation] =
    useState<string>('');
  const [selectedAutoSuggestOptions, setSelectedAutoSuggestOptions] =
    React.useState<MultiValue<AutoSuggestOption>>([]);
  // const [personsFilterError, setPersonsFilterError] = useState<boolean>(false);
  const accommodationDetailsBasic = useAppSelector(
    selectAccommodationDetailsBasic,
  );

  const hasRequiredPersonFilter = useCallback(
    (filter = searchFilter) =>
      !isTravelAgencyOrDomainUser || Number(filter.maxPersonCount ?? 0) > 0,
    [isTravelAgencyOrDomainUser, searchFilter],
  );

  useEffect(() => {
    dispatch(fetchAttributes(currentLocale));
  }, [dispatch, currentLocale]);

  // Clear searchResultItems when new dates are selected
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (selectedDates[0]) {
      params.set('startDate', selectedDates[0].toISOString());
    } else {
      params.delete('startDate');
    }
    if (selectedDates[1]) {
      params.set('endDate', selectedDates[1].toISOString());
    } else {
      params.delete('endDate');
    }
    history({
      search: params.toString(),
    });

    // Clear search results
    dispatch(clearSearchResults());
  }, [selectedDates, history, dispatch]);
  const handleEquipmentInputChanges = (event, typeName, mainAttributeId) => {
    const { target } = event;

    if (typeName === 'Boolean') {
      const propertyName = 'booleanAttributes';
      const selectedAttributes = [...searchFilter[propertyName]];
      const attributeId = target.name;

      if (target.checked) {
        selectedAttributes.push(attributeId);
      } else {
        const index = selectedAttributes.indexOf(attributeId);
        if (index !== -1) {
          selectedAttributes.splice(index, 1);
        }
      }

      dispatch(
        setSearchFilters({
          ...searchFilter,
          [propertyName]: selectedAttributes,
        }),
      );
    }

    /*
    if (typeName === 'MultiSelect') {
      const propertyName = 'multiSelectAttributes';
      const selectedAttributes = [...searchFilter[propertyName]];
      const attributeId = target.name;

      if (target.checked) {
        selectedAttributes.push({
          attributeId: mainAttributeId,
          optionId: attributeId,
        });
      } else {
        const index = selectedAttributes.findIndex(
          attr => attr.optionId === attributeId,
        );
        if (index !== -1) {
          selectedAttributes.splice(index, 1);
        }
      }

      dispatch(
        setSearchFilters({
          ...searchFilter,
          [propertyName]: selectedAttributes,
        }),
      );
    }
    */

    if (typeName === 'FreeText') {
      const propertyName = 'textAttributes';
      throw new Error('Not implemented');
    }
  };

  const handleEquipmentSelectChanges = (selectedOptions, attribute) => {
    const propertyName = 'multiSelectAttributes';

    // Get existing selections for other attributes
    const otherAttributeSelections = searchFilter.multiSelectAttributes.filter(
      attr => attr.attributeId !== attribute.attributeId,
    );

    // Create selections for current attribute
    const currentAttributeSelections = selectedOptions.map(option => ({
      attributeId: attribute.attributeId,
      optionId: option.value,
    }));

    // Combine both selections
    const allSelections = [
      ...otherAttributeSelections,
      ...currentAttributeSelections,
    ];

    dispatch(
      setSearchFilters({
        ...searchFilter,
        [propertyName]: allSelections,
      }),
    );
  };

  const handleDistanceInputChanges = (value, attributeId, typeName) => {
    if (typeName === 'NumericMin') {
      const propertyName = 'minimumNumericAttributes';
      const selectedAttributes = { ...searchFilter[propertyName] };

      if (value > 0) {
        selectedAttributes[attributeId] = value;
      } else if (
        Object.prototype.hasOwnProperty.call(selectedAttributes, attributeId)
      ) {
        // Remove the attribute if it exists
        delete selectedAttributes[attributeId];
      }

      // Update the search filter with the modified MinimumNumericAttributes
      dispatch(
        setSearchFilters({
          ...searchFilter,
          [propertyName]: selectedAttributes,
        }),
      );
    }

    if (typeName === 'NumericMax') {
      const propertyName = 'maximumNumericAttributes';
      const selectedAttributes = { ...searchFilter[propertyName] };

      if (value > 0) {
        selectedAttributes[attributeId] = value;
      } else if (
        Object.prototype.hasOwnProperty.call(selectedAttributes, attributeId)
      ) {
        // Remove the attribute if it exists
        delete selectedAttributes[attributeId];
      }

      // Update the search filter with the modified MinimumNumericAttributes
      dispatch(
        setSearchFilters({
          ...searchFilter,
          [propertyName]: selectedAttributes,
        }),
      );
    }
  };

  const handleSelect = (selectedItems: MultiValue<AutoSuggestOption>) => {
    const uniqueItems = selectedItems.reduce<AutoSuggestOption[]>(
      (acc, item) => {
        if (!acc.some(existingItem => existingItem.value === item.value)) {
          acc.push(item);
        }
        return acc;
      },
      [],
    );

    const autosuggestFiltering = {
      ...searchFilter,
      autosuggestions: uniqueItems,
    };

    setSelectedAutoSuggestOptions(uniqueItems);
    dispatch(setSearchFilters(autosuggestFiltering));
  };

  const handleSearchTermChange = (inputValue: string) => {
    if (inputValue !== '') {
      const searchStringFiltering = {
        ...searchFilter,
        searchString: inputValue,
      };

      dispatch(setSearchFilters(searchStringFiltering));
    }
    setSearchTerm(inputValue);
  };

  const handleSelectFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    // if (name === 'maxPersonCount' && Number(value) > 0) {
    //   setPersonsFilterError(false);
    // }
    dispatch(
      setSearchFilters({
        ...searchFilter,
        [name]: value,
      }),
    );
  };

  const handleSearchFilterChange = (field: string, value: unknown) => {
    const formattedValue =
      value instanceof Date ? format(value, 'yyyy-MM-dd') : value;

    dispatch(
      setSearchFilters({
        ...searchFilter,
        [field]: formattedValue,
      }),
    );
  };

  const handleRangeFilterChange = (name: string, value: number) => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        [name]: value,
      }),
    );
  };

  const handleBooleanFilterChange = (field: string, value: unknown) => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        [field]: value,
      }),
    );
  };

  // Clear searchResultItems when search filters are cleared
  const doClear = () => {
    setSearchTerm('');
    setSelectedAutoSuggestOptions([]);
    setSelectedDates([null, null]);
    dispatch(resetFilters());
    dispatch(clearSearchResults()); // Clear search results
  };

  const pad = (n: number) => n.toString().padStart(2, '0');

  const toLocalIsoLike = (d: Date) => {
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const formatDateTime = date => {
    if (!date) return null;

    // Set the desired time locally (e.g., 16:00:00)
    date.setHours(16, 0, 0, 0); // 16:00:00, with milliseconds set to 0

    // Build a local ISO-like string without timezone conversion
    // Take only YYYY-MM-DDTHH:MM:SS in local time
    return toLocalIsoLike(date);
  };

  const formatDateTimeEnd = date => {
    if (!date) return null;

    // Set the desired time locally (e.g., 12:00:00)
    date.setHours(12, 0, 0, 0);

    // Build a local ISO-like string without timezone conversion
    // Take only YYYY-MM-DDTHH:MM:SS in local time
    return toLocalIsoLike(date);
  };

  function findSeasonRatesByDateAndDuration(startDate, duration, seasonRates) {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + duration - 1); // Calculate the end date based on duration

    return seasonRates.filter(seasonRate => {
      const seasonStart = new Date(seasonRate.startDate);
      const seasonEnd = new Date(seasonRate.endDate);

      // Check if there is any overlap between the booking period and the rate period
      return start <= seasonEnd && end >= seasonStart;
    });
  }

  function getPriceByDuration(duration, rate) {
    if (rate && rate.ratePrices) {
      const price = rate.ratePrices.find(item => item.duration === duration);
      return price || 'Price data not found for the given duration';
    }
    return 'Rate data not found for the given duration';
  }

  const handleSportumBookingFlow = useCallback(
    (id: string, basketContainerTemp: BookingContainer) => {
      return dispatch(getAccommodationDetailsBasicAsync(id))
        .then(result => {
          if (getAccommodationDetailsBasicAsync.fulfilled.match(result)) {
            const accommodationDetails = result.payload;

            if (accommodationDetails.sportumId) {
              console.log(
                'This accommodation has Sportum ID:',
                accommodationDetails.sportumId,
              );

              // const basketContainerForSportumBooking = basketContainer;
              const bookingId = basketContainerTemp.bookingResponses[0].id;

              if (bookingId) {
                return dispatch(makeSportumTempBookingAsync(bookingId)).then(
                  res3 => {
                    const sportumResponse = res3.payload;
                    if (sportumResponse.isSuccess) {
                      console.log('Temporary Sportum booking done');
                      return {
                        success: true,
                        sportumId: accommodationDetails.sportumId,
                      };
                    }
                    return {
                      success: false,
                      sportumId: accommodationDetails.sportumId,
                    };
                  },
                );
              }
              // Add return here if bookingId is falsy
              return Promise.resolve({
                success: false,
                sportumId: accommodationDetails.sportumId,
              });
            }
            // console.log('This accommodation does not have Sportum ID');
            return Promise.resolve({ success: true, sportumId: null });
          }
          console.error(
            'Failed to fetch accommodation details:',
            result.payload,
          );
          return Promise.reject(
            new Error('Failed to fetch accommodation details'),
          );
        })
        .catch(error => {
          console.error('Error fetching accommodation details:', error);
          return Promise.reject(error);
        });
    },
    [dispatch],
  );

  const handleAddToBookingContainer = (id: string) => {
    setAddingToBasket(prev => new Set([...prev, id]));
    const item = searchResultItems.find(x => x.id === id);
    const tempContainer = {
      ...basketContainer,
    };

    let dailyPurchasePrice = 0;

    dispatch(getSeasonRatesAsync({ accommodationId: id })).then(result => {
      const arrival = selectedDates[0]
        ? selectedDates[0]
        : new Date(new Date().setDate(new Date().getDate() + 2));
      const duration =
        searchFilter.lengthOfStay > 0 ? searchFilter.lengthOfStay : 1;

      const currentRates = findSeasonRatesByDateAndDuration(
        arrival,
        duration,
        result.payload,
      );
      const priceItem = getPriceByDuration(duration, currentRates[0]);
      dailyPurchasePrice = priceItem.dailyPurchasePrice;
    });
    const arrival = selectedDates[0]
      ? selectedDates[0]
      : new Date(new Date().setDate(new Date().getDate() + 2));
    const departure = selectedDates[1]
      ? selectedDates[1]
      : new Date(new Date().setDate(new Date().getDate() + 3));

    const newAccommodationOrderItem: BookingDetailsOrderItem = {
      id, // replace with actual value
      isCancelled: false, // replace with actual value
      orderItemType: 2, // replace with actual value
      accommodationNumber: item.accommodationNumber, // replace with actual value
      accommodationId: '',
      addOnId: '', // replace with actual value
      arrivalDateTime: formatDateTime(arrival), // '2024-11-18T16:00:00', // replace with actual value
      departureDateTime: formatDateTimeEnd(departure), // replace with actual value
      startDateTime: formatDateTime(arrival), // '2024-11-18T16:00:00', // replace with actual value
      endDateTime: formatDateTimeEnd(departure), // '2024-11-21T12:00:00', // replace with actual value
      itemName: item.name, // replace with actual value,
      careTakers: [],
      optiwattiData: {
        accommodationBookingOrderItemId: '', // replace with actual value
        eventGuid: '', // replace with actual value
        status: '', // replace with actual value
        message: '', // replace with actual value
        translatedStatus: '', // replace with actual value
      },
      pricesResponse: {
        grossPrice: item.price, // 1624.9958, // replace with actual value
        grossPriceVat: 0, // replace with actual value
        finalBillNetPrice: 0, // replace with actual value
        netPrice: 0, // replace with actual value
        finalBillPrice: 0, // replace with actual value
        finalBillNetPriceWithoutVat: 0,
        finalBillNetPriceVat: 0, // replace with actual value
        discount: 0, // replace with actual value
        discountP: 0, // replace with actual value
        comission: 0, // replace with actual value
        comissionP: 0, // replace with actual value
        travelAgencyComission: 0, // replace with actual value
        travelAgencyComissionP: 0, // replace with actual value
        travelAgencyOnlyComission: 0, // replace with actual value
        sellerCommission: 0, // replace with actual value
        finalBillPriceWithoutVat: 0, // replace with actual value
        finalBillPriceVat: 0, // replace with actual value
        advanceBillPrice: 0, // replace with actual value
        advanceBillPriceVat: 0, // replace with actual value
        advanceBillPriceWithoutVat: 0, // replace with actual value
        vatP: 0, // replace with actual value
        travelAgencyVatP: 0, // replace with actual value
      },
      purchasePricesResponse: {
        sitePurchasePrice: 0, // replace with actual value
        ownerPurchasePrice: item.price * 0.8, // replace with actual value
        sitePurchasePriceVat: 0, // replace with actual value
        ownerPurchasePriceVat: 0, // replace with actual value
      },
      includedInPrice: false, // replace with actual value
      amountOfAdults: 1, // replace with actual value
      amountOfChildren: 0, // replace with actual value
      units: 1, // replace with actual value
      accommodationAccountRoles: [],
    };
    console.log(newAccommodationOrderItem, arrival, departure);
    let addAccommodationItem: AddAccommodationToBookingContainerItem = {
      accommodationId: newAccommodationOrderItem.id,
      arrivalDateTime: newAccommodationOrderItem.arrivalDateTime,
      departureDateTime: newAccommodationOrderItem.departureDateTime,
      adults:
        searchFilter.maxPersonCount != null
          ? Number(searchFilter.maxPersonCount)
          : 1,
      childs: newAccommodationOrderItem.amountOfChildren,
      finalBillNetPrice:
        newAccommodationOrderItem.pricesResponse.finalBillNetPrice,
      finalBillPrice: newAccommodationOrderItem.pricesResponse.finalBillPrice,
      grossPrice: newAccommodationOrderItem.pricesResponse.grossPrice,
      netPrice: newAccommodationOrderItem.pricesResponse.netPrice,
      purchasePrice:
        newAccommodationOrderItem.purchasePricesResponse.ownerPurchasePrice,
      bookingContainerId:
        tempContainer !== undefined &&
        tempContainer !== null &&
        tempContainer.id !== undefined &&
        tempContainer.id !== null
          ? tempContainer.id
          : '',
      advanceBillGrossPrice:
        newAccommodationOrderItem.pricesResponse.advanceBillPrice,
      vatPerCent: newAccommodationOrderItem.pricesResponse.vatP,
    } as AddAccommodationToBookingContainerItem;
    if (
      addAccommodationItem.bookingContainerId === null ||
      addAccommodationItem.bookingContainerId === undefined
    ) {
      addAccommodationItem = {
        ...addAccommodationItem,
        bookingContainerId: '',
      };
    }
    dispatch(addAccommodationToBookingContainerAsync(addAccommodationItem))
      .then(res => {
        if (res.payload.isSuccess) {
          // console.log(
          //   'addAccommodationToBookingContainerAsync response booking id:',
          //   res.payload.result.bookingResponses[0].id,
          // );
          dispatch(setBasketContainer(res.payload.result)).then(res2 => {
            const basketContainerForSportumBooking =
              res2.payload as BookingContainer;
            handleSportumBookingFlow(id, basketContainerForSportumBooking)
              .then(() => {
                clearAddingToBasket(id);
              })
              .catch(error => {
                // Handle any errors
                console.error('Error in Sportum booking flow:', error);
                clearAddingToBasket(id);
              });
          });
        } else {
          clearAddingToBasket(id);
        }
      })
      .catch(err => {
        console.error('error happened in addind accommodation:', err);
        clearAddingToBasket(id);
      });
  };

  const handleAddToBookingContainerByPass = (id: string) => {
    setAddingToBasket(prev => new Set([...prev, id]));
    const item = searchResultItems.find(x => x.id === id);
    const tempContainer = {
      ...basketContainer,
    };

    let dailyPurchasePrice = 0;

    dispatch(getSeasonRatesAsync({ accommodationId: id })).then(result => {
      const arrival = selectedDates[0]
        ? selectedDates[0]
        : new Date(new Date().setDate(new Date().getDate() + 2));
      const duration =
        searchFilter.lengthOfStay > 0 ? searchFilter.lengthOfStay : 1;

      const currentRates = findSeasonRatesByDateAndDuration(
        arrival,
        duration,
        result.payload,
      );
      const priceItem = getPriceByDuration(duration, currentRates[0]);
      dailyPurchasePrice = priceItem.dailyPurchasePrice;
    });
    const arrival = selectedDates[0]
      ? selectedDates[0]
      : new Date(new Date().setDate(new Date().getDate() + 2));
    const departure = selectedDates[1]
      ? selectedDates[1]
      : new Date(new Date().setDate(new Date().getDate() + 3));

    const newAccommodationOrderItem: BookingDetailsOrderItem = {
      id, // replace with actual value
      isCancelled: false, // replace with actual value
      orderItemType: 2, // replace with actual value
      accommodationNumber: item.accommodationNumber, // replace with actual value
      accommodationId: '',
      addOnId: '', // replace with actual value
      arrivalDateTime: formatDateTime(arrival), // '2024-11-18T16:00:00', // replace with actual value
      departureDateTime: formatDateTimeEnd(departure), // replace with actual value
      startDateTime: formatDateTime(arrival), // '2024-11-18T16:00:00', // replace with actual value
      endDateTime: formatDateTimeEnd(departure), // '2024-11-21T12:00:00', // replace with actual value
      itemName: item.name, // replace with actual value,
      careTakers: [],
      optiwattiData: {
        accommodationBookingOrderItemId: '', // replace with actual value
        eventGuid: '', // replace with actual value
        status: '', // replace with actual value
        message: '', // replace with actual value
        translatedStatus: '', // replace with actual value
      },
      pricesResponse: {
        grossPrice: item.price, // 1624.9958, // replace with actual value
        grossPriceVat: 0, // replace with actual value
        finalBillNetPrice: 0, // replace with actual value
        netPrice: 0, // replace with actual value
        finalBillPrice: 0, // replace with actual value
        finalBillNetPriceWithoutVat: 0,
        finalBillNetPriceVat: 0,
        discount: 0, // replace with actual value
        discountP: 0, // replace with actual value
        comission: 0, // replace with actual value
        comissionP: 0, // replace with actual value
        travelAgencyComission: 0, // replace with actual value
        travelAgencyComissionP: 0, // replace with actual value
        travelAgencyOnlyComission: 0, // replace with actual value
        sellerCommission: 0, // replace with actual value
        finalBillPriceWithoutVat: 0, // replace with actual value
        finalBillPriceVat: 0, // replace with actual value
        advanceBillPrice: 0, // replace with actual value
        advanceBillPriceVat: 0, // replace with actual value
        advanceBillPriceWithoutVat: 0, // replace with actual value
        vatP: 0, // replace with actual value
        travelAgencyVatP: 0, // replace with actual value
      },
      purchasePricesResponse: {
        sitePurchasePrice: 0, // replace with actual value
        ownerPurchasePrice: item.price * 0.8, // replace with actual value
        sitePurchasePriceVat: 0, // replace with actual value
        ownerPurchasePriceVat: 0, // replace with actual value
      },
      includedInPrice: false, // replace with actual value
      amountOfAdults: 1, // replace with actual value
      amountOfChildren: 0, // replace with actual value
      units: 1, // replace with actual value
      accommodationAccountRoles: [],
    };
    console.log(newAccommodationOrderItem, arrival, departure);
    let addAccommodationItem: AddAccommodationToBookingContainerItem = {
      accommodationId: newAccommodationOrderItem.id,
      arrivalDateTime: newAccommodationOrderItem.arrivalDateTime,
      departureDateTime: newAccommodationOrderItem.departureDateTime,
      adults:
        searchFilter.maxPersonCount != null
          ? Number(searchFilter.maxPersonCount)
          : 1,
      childs: newAccommodationOrderItem.amountOfChildren,
      finalBillNetPrice:
        newAccommodationOrderItem.pricesResponse.finalBillNetPrice,
      finalBillPrice: newAccommodationOrderItem.pricesResponse.finalBillPrice,
      grossPrice: newAccommodationOrderItem.pricesResponse.grossPrice,
      netPrice: newAccommodationOrderItem.pricesResponse.netPrice,
      purchasePrice:
        newAccommodationOrderItem.purchasePricesResponse.ownerPurchasePrice,
      bookingContainerId:
        tempContainer !== undefined &&
        tempContainer !== null &&
        tempContainer.id !== undefined &&
        tempContainer.id !== null
          ? tempContainer.id
          : '',
      advanceBillGrossPrice:
        newAccommodationOrderItem.pricesResponse.advanceBillPrice,
      vatPerCent: newAccommodationOrderItem.pricesResponse.vatP,
      bypassRules: true,
    } as AddAccommodationToBookingContainerItem;
    if (
      addAccommodationItem.bookingContainerId === null ||
      addAccommodationItem.bookingContainerId === undefined
    ) {
      addAccommodationItem = {
        ...addAccommodationItem,
        bookingContainerId: '',
      };
    }
    dispatch(addAccommodationToBookingContainerAsync(addAccommodationItem))
      .then(res => {
        if (res.payload.isSuccess) {
          // console.log(
          //   'addAccommodationToBookingContainerAsync response booking id:',
          //   res.payload.result.bookingResponses[0].id,
          // );
          dispatch(setBasketContainer(res.payload.result)).then(res2 => {
            const basketContainerForSportumBooking =
              res2.payload as BookingContainer;
            handleSportumBookingFlow(id, basketContainerForSportumBooking)
              .then(() => {
                clearAddingToBasket(id);
              })
              .catch(error => {
                // Handle any errors
                console.error('Error in Sportum booking flow:', error);
                clearAddingToBasket(id);
              });
          });
        } else {
          clearAddingToBasket(id);
        }
      })
      .catch(err => {
        console.error('error happened in addind accommodation:', err);
        clearAddingToBasket(id);
      });
  };
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: ORDER_DIRECTION.Asc,
  });

  const handleSort = key => {
    // if (!hasRequiredPersonFilter()) {
    //   setPersonsFilterError(true);
    //   return;
    // }

    let direction = ORDER_DIRECTION.Asc;
    if (
      sortConfig.key === key &&
      sortConfig.direction === ORDER_DIRECTION.Asc
    ) {
      direction = ORDER_DIRECTION.Desc;
    }
    setSortConfig({ key, direction });
    // Fetch sorted data from the server
    const paginatedFiltering = {
      ...searchFilter,
      orderBy: key,
      orderDirection: direction,
      cultureOption: currentLocale,
    };

    // setPersonsFilterError(false);
    dispatch(setSearchFilters(paginatedFiltering));
    dispatch(searchBookableAccommodations(paginatedFiltering));
  };

  // const sortedItems = [...searchResultItems].sort((a, b) => {
  //   if (a.Items[sortConfig.key] < b.Items[sortConfig.key]) {
  //     return sortConfig.direction === ORDER_DIRECTION.Asc ? -1 : 1;
  //   }
  //   if (a.Items[sortConfig.key] > b.Items[sortConfig.key]) {
  //     return sortConfig.direction === ORDER_DIRECTION.Asc ? 1 : -1;
  //   }
  //   return 0;
  // });
  // console.log(searchResultItems);

  const goToBasket = () => {
    history(`/bookings/bookingcontainer/newContainer`, {
      state: {
        container: basketContainer,
        backUrl: `${location.pathname}${location.search}`,
      },
    });
  };

  const HandlePaginationClick = (Page: number) => {
    // if (!hasRequiredPersonFilter()) {
    //   setPersonsFilterError(true);
    //   return;
    // }

    const paginatedFiltering = { ...searchFilter, PageNumber: Page };
    // setPersonsFilterError(false);
    dispatch(setSearchFilters(paginatedFiltering));
    dispatch(searchBookableAccommodations(paginatedFiltering));
  };

  useEffect(() => {}, [searchResultItems]);

  useEffect(() => {
    if (freeTextUpdated) {
      // if (!hasRequiredPersonFilter()) {
      //   setPersonsFilterError(true);
      //   setFreeTextUpdated(false);
      //   return;
      // }

      // setPersonsFilterError(false);
      dispatch(searchBookableAccommodations(searchFilter));
      setFreeTextUpdated(false);
      setSubmitted(true);
    }
  }, [dispatch, searchFilter, freeTextUpdated, hasRequiredPersonFilter]);
  const doSearch = async event => {
    /*
    console.log('searchFilter', searchFilter);
    dispatch(searchBookableAccommodations(searchFilter)).then(res => {
      // searchResultItems
      console.log('searchResultItems', res);
    });
    */
    setFreeTextUpdated(true);
    event.preventDefault();

    const searchStringFiltering = {
      ...searchFilter,
      PageNumber: InitialPaginationParameters.PageNumber,
      searchString: searchTerm,
      cultureOption: currentLocale,
      includeBlockedByAvailabilityRules: isTravelAgencyUser
        ? false
        : searchFilter.includeBlockedByAvailabilityRules,
    };

    // if (!hasRequiredPersonFilter(searchStringFiltering)) {
    //   setPersonsFilterError(true);
    //   return;
    // }

    // setPersonsFilterError(false);
    await dispatch(setSearchFilters(searchStringFiltering));
  };

  useEffect(() => {}, [basketContainer]);

  const equipmentAttributesPerColumn = 12;
  const totalEquipmentAttributeColumns = Math.ceil(
    equipmentAttributes.length / equipmentAttributesPerColumn,
  );
  const distanceAttributesPerColumn = 7;
  const totalDistanceAttributeColumns = Math.ceil(
    distanceAttributes.length / distanceAttributesPerColumn,
  );
  const getSortIndicator = key => {
    if (sortConfig.key === key) {
      return sortConfig.direction === ORDER_DIRECTION.Asc ? '↑' : '↓';
    }
    return '';
  };

  const handleRemoveFilter = (filterKey: string) => {
    const updatedSearchFilter = { ...searchFilter };
    delete updatedSearchFilter[filterKey];

    dispatch(setSearchFilters(updatedSearchFilter));
  };

  const handleRemoveFilterValue = (filterKey: string, value: unknown) => {
    const updatedFilterCriteria = { ...searchFilter };

    if (Array.isArray(updatedFilterCriteria[filterKey])) {
      updatedFilterCriteria[filterKey] = updatedFilterCriteria[
        filterKey
      ].filter((item: unknown) => item !== value);
    }

    dispatch(setSearchFilters(updatedFilterCriteria));
  };

  const handleRemoveSearchDatesFilter = () => {
    setSelectedDates([null, null]);

    const updatedSearchFilter = { ...searchFilter };
    delete updatedSearchFilter.lengthOfStay;
    delete updatedSearchFilter.arrivalDateTime;

    dispatch(setSearchFilters(updatedSearchFilter));
  };

  const handleRemoveEquipmentFilter = (attributeId: string) => {
    const updatedBooleanAttributes = searchFilter.booleanAttributes.filter(
      id => id !== attributeId,
    );

    const updatedMultiSelectAttributes =
      searchFilter.multiSelectAttributes.filter(
        attr => attr.optionId !== attributeId,
      );

    dispatch(
      setSearchFilters({
        ...searchFilter,
        booleanAttributes: updatedBooleanAttributes,
        multiSelectAttributes: updatedMultiSelectAttributes,
      }),
    );
  };

  const handleRemoveDistanceFilter = (attributeId: string) => {
    const updatedMinimumNumericAttributes = {
      ...searchFilter.minimumNumericAttributes,
    };
    const updatedMaximumNumericAttributes = {
      ...searchFilter.maximumNumericAttributes,
    };

    delete updatedMinimumNumericAttributes[attributeId];
    delete updatedMaximumNumericAttributes[attributeId];

    dispatch(
      setSearchFilters({
        ...searchFilter,
        minimumNumericAttributes: updatedMinimumNumericAttributes,
        maximumNumericAttributes: updatedMaximumNumericAttributes,
      }),
    );
  };

  const getDistanceAttributeNameById = attributeId => {
    const attribute = distanceAttributes.find(
      attr => attr.attributeId === attributeId,
    );
    return attribute ? attribute.name : attributeId;
  };

  const getEquipmentAttributeNameById = attributeId => {
    const attribute = equipmentAttributes.find(
      attr => attr.attributeId === attributeId,
    );
    return attribute ? attribute.name : attributeId;
  };

  const getMultiSelectAttributeNameById = (attributeId, optionId) => {
    const attribute = equipmentAttributes.find(
      attr => attr.attributeId === attributeId,
    );
    if (attribute) {
      const option = attribute.multiSelectOptions.find(
        opt => opt.optionId === optionId,
      );
      return option ? option.name : optionId;
    }
    return attributeId;
  };

  const formatSelectedValue = (label, value, filterKey: string) => {
    if (value === null || value === undefined || value === 0 || value === '0') {
      return null;
    }

    let displayValue = value;
    if (typeof value === 'boolean') {
      displayValue = value ? t('common:texts.yes') : t('common:texts.no');
    }

    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {displayValue}
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleRemoveFilter(filterKey)}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  const formatSelectedValues = (label, values, filterKey: string) => {
    if (!values || values.length === 0) {
      return null;
    }

    return (
      <>
        <span className="small fw-medium ms-8">{label}:</span>
        {values.map(value => (
          <span
            key={value}
            className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8"
          >
            {value}
            <Button
              variant="link"
              className="p-0"
              onClick={() => handleRemoveFilterValue(filterKey, value)}
            >
              <span className="bi bi-x" />
            </Button>
          </span>
        ))}
      </>
    );
  };

  const formatSearchDates = (label: string, value: Date, value2: Date) => {
    if (
      value === null ||
      value2 === null ||
      value === undefined ||
      value2 === undefined ||
      value < new Date('2020-01-01') ||
      value2 < new Date('2020-01-01')
    ) {
      return null;
    }

    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {dateToDateOnlyString(value)} - {dateToDateOnlyString(value2)}
        <Button
          variant="link"
          className="p-0"
          onClick={() => {
            handleRemoveSearchDatesFilter();
          }}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  const formatSelectedEquipments = (
    label,
    equipments,
    multiSelectEquipments,
  ) => {
    if (
      (!equipments || equipments.length === 0) &&
      (!multiSelectEquipments || multiSelectEquipments.length === 0)
    ) {
      return null;
    }

    const booleanAttributes = equipments.map(id => ({
      id,
      name: getEquipmentAttributeNameById(id).toLowerCase(),
    }));

    const multiSelectAttributes = multiSelectEquipments.map(attr => ({
      id: attr.optionId,
      name: getMultiSelectAttributeNameById(
        attr.attributeId,
        attr.optionId,
      ).toLowerCase(),
    }));

    const allAttributes = [...booleanAttributes, ...multiSelectAttributes];

    return (
      <>
        <span className="small fw-medium ms-8">{label}:</span>
        {allAttributes.map(attribute => (
          <span
            key={attribute.id}
            className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8"
          >
            {attribute.name}
            <Button
              variant="link"
              className="p-0"
              onClick={() => handleRemoveEquipmentFilter(attribute.id)}
            >
              <span className="bi bi-x" />
            </Button>
          </span>
        ))}
      </>
    );
  };

  const formatSelectedDistances = (label, distances) => {
    if (!distances || Object.keys(distances).length === 0) {
      return null;
    }
    return (
      <>
        <span className="small fw-medium ms-8">{label}:</span>
        {Object.entries(distances).map(([key, value]) => (
          <span
            key={key}
            className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8"
          >
            {getDistanceAttributeNameById(key).toLowerCase()}: {value} km
            <Button
              variant="link"
              className="p-0"
              onClick={() => handleRemoveDistanceFilter(key)}
            >
              <span className="bi bi-x" />
            </Button>
          </span>
        ))}
      </>
    );
  };
  // Filter out isBlockedByAvailabilityRules items from the results
  const searchResultItemsList = searchResultItems
    ?.filter(item => !isTravelAgencyUser || !item.isBlockedByAvailabilityRules)
    ?.map(item => (
      <React.Fragment key={item.id}>
        <tr>
          <td style={{ width: '150px' }} className="px-16">
            <div className="ratio ratio-3x2">
              {item.mainImageUrl && (
                <img
                  src={item.mainImageUrl}
                  alt="Accommodation"
                  className="rounded w-100"
                />
              )}
            </div>
          </td>
          <td>
            <span className="d-block">
              {isTravelAgencyOrDomainUser && item.accommodationNumber}
              {!isTravelAgencyOrDomainUser && (
                <Link
                  to={`/accommodations/details/${item.id}?tab=Varaustilanne`}
                  target="_blank"
                  style={{ textDecoration: 'none' }}
                >
                  {item.accommodationNumber}
                </Link>
              )}
            </span>
          </td>
          <td>
            <div className="d-grid gap-4">
              <span className="d-block fw-medium">
                {isTravelAgencyOrDomainUser && (
                  <button
                    key={`accommodation-${item.id}`}
                    type="submit"
                    className="booking-link bg-transparent p-0 text-start border-0"
                    onClick={() => {
                      setAgentViewableAccommodation(item.accommodationSlug);
                      setIsAgentAccommodationModalOpen(true);
                    }}
                  >
                    {item.name}
                  </button>
                )}
                {!isTravelAgencyOrDomainUser && (
                  <Link
                    to={`/accommodations/details/${item.id}?tab=Varaustilanne`}
                    target="_blank"
                    style={{ textDecoration: 'none' }}
                  >
                    {item.name}
                  </Link>
                )}
              </span>
              <span className="d-block" style={{ color: 'rgb(28, 81, 182)' }}>
                {[
                  item.regionName && `${item.regionName}`,
                  item.skiResortName && `${item.skiResortName}`,
                  item.municipalityName && `${item.municipalityName}`,
                  item.city &&
                    item.city !== item.municipalityName &&
                    `${item.city}`,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </span>
              {item.includedAddonNames &&
                item.includedAddonNames.length > 0 && (
                  <span>
                    {t('searchBookableAccommodations:results.item.includes')}:{' '}
                    {item.includedAddonNames.join(', ')}
                  </span>
                )}
              {item.customerFeedbackData?.averageAnswerValue > 0 &&
                item.customerFeedbackData?.feedbackCount > 0 && (
                  <span>
                    {t(
                      'searchBookableAccommodations:results.item.feedbackRating',
                    )}{' '}
                    <span className="fw-medium">{`${item.customerFeedbackData.averageAnswerValue}`}</span>
                    , {`${item.customerFeedbackData.feedbackCount}`}{' '}
                    {t(
                      'searchBookableAccommodations:results.item.feedbackCountEnding',
                    )}
                  </span>
                )}
              {item.nearbyLocationData && (
                <span
                  style={{ color: '#fdc300' }}
                >{`${t('searchBookableAccommodations:results.item.distanceText')} ${item.nearbyLocationData.targetLocationName} ${item.nearbyLocationData.distance} km.`}</span>
              )}
              <div className="d-flex align-items-center pt-8">
                <div className="d-flex flex-wrap align-items-center gap-4">
                  <span className="badge badge-sm bg-light">{`${t('searchBookableAccommodations:results.item.preparationDays')}: ${item.defaultPreparationDays ?? 0} vrk`}</span>
                  <span className="badge badge-sm bg-light">{`${item.maxPersons ?? 0} ${t('searchBookableAccommodations:results.item.persons')}`}</span>
                  {item.bedrooms > 0 && (
                    <span className="badge badge-sm bg-light">{`${item.bedrooms} ${t('searchBookableAccommodations:results.item.bedrooms')}`}</span>
                  )}
                  <span className="badge badge-sm bg-light">{`${item.beds} + ${item.extraBeds} ${t('searchBookableAccommodations:results.item.extrabeds')}`}</span>
                  {item.area > 0 && (
                    <span className="badge badge-sm bg-light">{`${t('searchBookableAccommodations:results.item.area')} ${item.area} m2`}</span>
                  )}
                  {item.distanceToSkiResortInKilometers > 0 && (
                    <span className="badge badge-sm bg-light">{`${t('searchBookableAccommodations:results.item.skiresort')} ${item.distanceToSkiResortInKilometers} km`}</span>
                  )}
                </div>
              </div>
            </div>
          </td>
          <td>
            <span className="d-block">
              {selectedDates[0] && selectedDates[1]
                ? `${selectedDates[0].toLocaleDateString('fi-FI')} - ${selectedDates[1].toLocaleDateString('fi-FI')}`
                : ''}
              {!isTravelAgencyUser && item.lastMinuteRuleData && (
                <>
                  <br />
                  {item.lastMinuteRuleData.percentage !== 0 && (
                    <span style={{ color: 'green' }}>
                      {`${t('searchBookableAccommodations:results.item.lastMinuteRule')} ${item.lastMinuteRuleData.percentage} %, ${t('searchBookableAccommodations:results.item.valid')} ${item.lastMinuteRuleData.daysUntilStart} ${t('searchBookableAccommodations:results.item.datesFromToday')}`}
                    </span>
                  )}
                  {item.lastMinuteRuleData.difference !== 0 && (
                    <span style={{ color: 'green' }}>
                      {`${t('searchBookableAccommodations:results.item.lastMinuteRule')} ${item.lastMinuteRuleData.difference} €, ${t('searchBookableAccommodations:results.item.valid')} ${item.lastMinuteRuleData.daysUntilStart} ${t('searchBookableAccommodations:results.item.datesFromToday')}`}
                    </span>
                  )}
                </>
              )}
              {item.isBlockedByAvailabilityRules && (
                <>
                  <br />
                  <span style={{ color: 'red' }}>
                    {t(
                      'searchBookableAccommodations:results.item.blockedByRules',
                    )}
                  </span>
                  {item.blockingMessages &&
                    item.blockingMessages.length > 0 && (
                      <ul
                        style={{
                          color: 'red',
                          marginTop: '8px',
                          paddingLeft: '16px',
                        }}
                      >
                        {item.blockingMessages.map(message => (
                          <li key={`${item.id}-${message}`}>{message}</li>
                        ))}
                      </ul>
                    )}
                </>
              )}
            </span>
          </td>
          {!isTravelAgencyUser && item.originalPriceForTravelAgency > 0 && (
            <td>
              <span className="d-block">
                <span style={{ textDecoration: 'line-through' }}>
                  {item.originalPriceForTravelAgency.toFixed(2)}
                </span>
                <br />
                {item.price > 0 ? item.price.toFixed(2) : ''}
              </span>
            </td>
          )}
          {isTravelAgencyUser && item.originalPriceForTravelAgency > 0 && (
            <td>
              <span className="d-block">
                {item.price > 0 ? item.price.toFixed(2) : ''}
              </span>
            </td>
          )}
          {item.originalPriceForTravelAgency === 0 && (
            <td>
              <span className="d-block">
                {item.price > 0 ? `${item.price} €` : ''}
              </span>
            </td>
          )}
          {!isTravelAgencyOrDomainUser && (
            <td>
              {item.ownerNumber ?? ''}
              <br />
              {item.ownerName ?? ''}
            </td>
          )}
          {!isTravelAgencyUser && (
            <td style={{ width: 48 }}>
              {!item.isBlockedByAvailabilityRules && item.price > 0 && (
                <Button
                  size="sm"
                  variant="primary"
                  className="text-nowrap"
                  disabled={addingToBasket.has(item.id)}
                  onClick={() => handleAddToBookingContainer(item.id)}
                >
                  {addingToBasket.has(item.id) && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      style={{ marginRight: 4, verticalAlign: 'middle' }}
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        fill="none"
                        stroke="rgba(0,0,0,0.2)"
                        strokeWidth="3"
                      />
                      <path
                        d="M12 3 A9 9 0 0 1 21 12"
                        fill="none"
                        stroke="rgba(0,0,0,0.75)"
                        strokeWidth="3"
                        strokeLinecap="round"
                      >
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 12 12"
                          to="360 12 12"
                          dur="0.75s"
                          repeatCount="indefinite"
                        />
                      </path>
                    </svg>
                  )}
                  {t('searchBookableAccommodations:booking.addToBasket')}
                </Button>
              )}
              {item.isBlockedByAvailabilityRules && item.price > 0 && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={addingToBasket.has(item.id)}
                  onClick={() => handleAddToBookingContainerByPass(item.id)}
                >
                  {addingToBasket.has(item.id) && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      style={{ marginRight: 4, verticalAlign: 'middle' }}
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        fill="none"
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth="3"
                      />
                      <path
                        d="M12 3 A9 9 0 0 1 21 12"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3"
                        strokeLinecap="round"
                      >
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 12 12"
                          to="360 12 12"
                          dur="0.75s"
                          repeatCount="indefinite"
                        />
                      </path>
                    </svg>
                  )}
                  {t(
                    'searchBookableAccommodations:booking.skipRulesAddToBasket',
                  )}
                </Button>
              )}
            </td>
          )}
          {isTravelAgencyUser &&
            basketContainer.bookingResponses.length <= 0 && (
              <td style={{ width: 48 }}>
                {!item.isBlockedByAvailabilityRules && item.price > 0 && (
                  <Button
                    size="sm"
                    variant="primary"
                    className="text-nowrap"
                    disabled={addingToBasket.has(item.id)}
                    onClick={() => handleAddToBookingContainer(item.id)}
                  >
                    {addingToBasket.has(item.id) && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        style={{ marginRight: 4, verticalAlign: 'middle' }}
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          fill="none"
                          stroke="rgba(0,0,0,0.2)"
                          strokeWidth="3"
                        />
                        <path
                          d="M12 3 A9 9 0 0 1 21 12"
                          fill="none"
                          stroke="rgba(0,0,0,0.75)"
                          strokeWidth="3"
                          strokeLinecap="round"
                        >
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 12 12"
                            to="360 12 12"
                            dur="0.75s"
                            repeatCount="indefinite"
                          />
                        </path>
                      </svg>
                    )}
                    {t('searchBookableAccommodations:booking.addToBasket')}
                  </Button>
                )}
                {item.isBlockedByAvailabilityRules && item.price > 0 && (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={addingToBasket.has(item.id)}
                    onClick={() => handleAddToBookingContainerByPass(item.id)}
                  >
                    {addingToBasket.has(item.id) && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        style={{ marginRight: 4, verticalAlign: 'middle' }}
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          fill="none"
                          stroke="rgba(255,255,255,0.3)"
                          strokeWidth="3"
                        />
                        <path
                          d="M12 3 A9 9 0 0 1 21 12"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="3"
                          strokeLinecap="round"
                        >
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 12 12"
                            to="360 12 12"
                            dur="0.75s"
                            repeatCount="indefinite"
                          />
                        </path>
                      </svg>
                    )}
                    {t(
                      'searchBookableAccommodations:booking.skipRulesAddToBasket',
                    )}
                  </Button>
                )}
              </td>
            )}
        </tr>
        {searchFilter.logPriceCalculation &&
          item.priceCalculationLogs &&
          item.priceCalculationLogs.map(log => (
            <tr
              key={`${item.id}-log-${log}`}
              style={{ margin: '4px 0', padding: '2px 0' }}
            >
              <td colSpan={7} style={{ paddingLeft: '20px', padding: '4px 0' }}>
                {log}
              </td>
            </tr>
          ))}
      </React.Fragment>
    ));

  return (
    <>
      <Helmet>
        <title>{t('searchBookableAccommodations:title')}</title>
      </Helmet>
      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={[t('searchBookableAccommodations:header.breadcrumb')]}
          Title={t('searchBookableAccommodations:header.title')}
        />
        <AccommodationAutoCompleteSearchBar
          onSelect={handleSelect}
          selectedOptions={selectedAutoSuggestOptions}
          isLoading={loadingStatus === LOADING_STATUS.LOADING}
          searchHandler={doSearch}
          handleSearchTermChange={handleSearchTermChange}
          searchTerm={searchTerm}
        >
          {/* {personsFilterError && ( */}
          {/*  <div className="position-relative px-24 pt-0 pb-8 text-danger mt-2"> */}
          {/*    {t('searchBookableAccommodations:filters.personAmountRequired')} */}
          {/*  </div> */}
          {/* )} */}
          {loadingStatus === LOADING_STATUS.IDLE && (
            <div className="position-relative px-24 pt-0 pb-8">
              <BasicSearchFilters
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                searchFilter={searchFilter}
                handleSelectFilterChange={handleSelectFilterChange}
                handleSearchFilterChange={handleSearchFilterChange}
                handleRangeFilterChange={handleRangeFilterChange}
                handleBooleanFilterChange={handleBooleanFilterChange}
              />
              <SearchFilterDropdown
                DefaultValue={false}
                FullWidth
                Id="accommodations-equipment"
                Label={t('searchBookableAccommodations:filters.facility')}
                MinWidth={600}
                Value={
                  searchFilter.booleanAttributes.length > 0 ||
                  searchFilter.multiSelectAttributes.length > 0
                }
                ShowCloseButton={false}
              >
                <Row xs={1} md={2}>
                  {[...Array(totalEquipmentAttributeColumns)].map(
                    (_, columnIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Col key={columnIndex}>
                        {equipmentAttributes
                          .slice(
                            columnIndex * equipmentAttributesPerColumn,
                            (columnIndex + 1) * equipmentAttributesPerColumn,
                          )
                          .map(attribute => (
                            <React.Fragment key={attribute.attributeId}>
                              {attribute.typeName === 'MultiSelect' ? (
                                <>
                                  <FormLabel>{attribute.name}</FormLabel>
                                  <div
                                    style={{
                                      marginLeft: '20px',
                                      marginBottom: '20px',
                                    }}
                                  >
                                    {/*
                                    {attribute.multiSelectOptions?.map(
                                      option => (
                                        <InputCheck
                                          key={option.optionId}
                                          HandleChange={(
                                            e: AccommodationAttribute,
                                          ) =>
                                            handleEquipmentInputChanges(
                                              e,
                                              attribute.typeName,
                                              attribute.attributeId,
                                            )
                                          }
                                          Id={`accommodations-equipment-${option.optionId}`}
                                          Name={option.optionId}
                                          Label={option.name}
                                          IsRequired={false}
                                          Checked={searchFilter.multiSelectAttributes.some(
                                            attr =>
                                              attr.optionId === option.optionId,
                                          )}
                                        />
                                      ),
                                    )}
                                    */}
                                    <Select
                                      isMulti
                                      placeholder={t(
                                        'searchBookableAccommodations:filters.select',
                                      )}
                                      options={attribute.multiSelectOptions.map(
                                        option => ({
                                          label: option.name,
                                          value: option.optionId,
                                        }),
                                      )}
                                      value={searchFilter.multiSelectAttributes
                                        .filter(
                                          attr =>
                                            attr.attributeId ===
                                            attribute.attributeId,
                                        )
                                        .map(attr => ({
                                          label:
                                            attribute.multiSelectOptions.find(
                                              option =>
                                                option.optionId ===
                                                attr.optionId,
                                            )?.name,
                                          value: attr.optionId,
                                        }))}
                                      onChange={selectedOptions =>
                                        handleEquipmentSelectChanges(
                                          selectedOptions,
                                          attribute,
                                        )
                                      }
                                    />
                                  </div>
                                </>
                              ) : (
                                <InputCheck
                                  key={attribute.attributeId}
                                  HandleChange={(e: AccommodationAttribute) =>
                                    handleEquipmentInputChanges(
                                      e,
                                      attribute.typeName,
                                      attribute.attributeId,
                                    )
                                  }
                                  Id={`accommodations-equipment-${attribute.attributeId}`}
                                  Name={attribute.attributeId}
                                  Label={attribute.name}
                                  IsRequired={false}
                                  Checked={searchFilter.booleanAttributes.includes(
                                    attribute.attributeId,
                                  )}
                                />
                              )}
                            </React.Fragment>
                          ))}
                      </Col>
                    ),
                  )}
                </Row>
              </SearchFilterDropdown>
              <SearchFilterDropdown
                DefaultValue={false}
                FullWidth
                Id="accommodations-distances"
                Label={t('searchBookableAccommodations:filters.distance')}
                MinWidth={600}
                Value={
                  Object.keys(searchFilter.minimumNumericAttributes).length >
                    0 ||
                  Object.keys(searchFilter.maximumNumericAttributes).length > 0
                }
                ShowCloseButton={false}
              >
                <Row xs={1} md={2}>
                  {[...Array(totalDistanceAttributeColumns)].map(
                    (_, columnIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Col key={columnIndex}>
                        {distanceAttributes
                          .slice(
                            columnIndex * distanceAttributesPerColumn,
                            (columnIndex + 1) * distanceAttributesPerColumn,
                          )
                          .map(attribute => (
                            <React.Fragment key={attribute.attributeId}>
                              <Form.Label>
                                {attribute.name}
                                {attribute.typeName === 'NumericMin' &&
                                  ` ${t('basicSearchFilters:filters.atLeast')}`}
                              </Form.Label>
                              <InputRange
                                HandleChange={(e: number) =>
                                  handleDistanceInputChanges(
                                    e,
                                    attribute.attributeId,
                                    attribute.typeName,
                                  )
                                }
                                Id={`accommodations-distances-${attribute.attributeId}`}
                                Name={attribute.attributeId}
                                Max={30}
                                Min={0}
                                Step={0.1}
                                Unit="km"
                                Value={
                                  attribute.typeName === 'NumericMin'
                                    ? searchFilter.minimumNumericAttributes[
                                        attribute.attributeId
                                      ] || 0
                                    : searchFilter.maximumNumericAttributes[
                                        attribute.attributeId
                                      ] || 0
                                }
                              />
                            </React.Fragment>
                          ))}
                      </Col>
                    ),
                  )}
                </Row>
              </SearchFilterDropdown>
            </div>
          )}

          {loadingStatus === LOADING_STATUS.IDLE && (
            <>
              <hr className="my-0" />
              <div className="d-flex align-items-center p-24">
                <div className="d-flex flex-wrap align-items-center gap-8">
                  {formatSearchDates(
                    t(
                      'searchBookableAccommodations:selectedFilters.chosenDates',
                    ),
                    selectedDates[0],
                    selectedDates[1],
                  )}
                  {formatSelectedValue(
                    t(
                      'searchBookableAccommodations:selectedFilters.personAmount',
                    ),
                    searchFilter.maxPersonCount,
                    'maxPersonCount',
                  )}
                  {formatSelectedValue(
                    t('searchBookableAccommodations:selectedFilters.area'),
                    searchFilter.area,
                    'area',
                  )}
                  {formatSelectedValue(
                    t('searchBookableAccommodations:selectedFilters.price'),
                    searchFilter.maxPrice,
                    'maxPrice',
                  )}
                  {formatSelectedValue(
                    t(
                      'searchBookableAccommodations:selectedFilters.numberOfBedrooms',
                    ),
                    searchFilter.numberOfBedrooms,
                    'numberOfBedrooms',
                  )}
                  {formatSelectedValue(
                    t(
                      'searchBookableAccommodations:selectedFilters.numberOfToilets',
                    ),
                    searchFilter.numberOfToilets,
                    'numberOfToilets',
                  )}
                  {formatSelectedValues(
                    t(
                      'searchBookableAccommodations:selectedFilters.qualityRating',
                    ),
                    searchFilter.qualityRatings,
                    'qualityRatings',
                  )}
                  {formatSelectedValue(
                    t(
                      'searchBookableAccommodations:selectedFilters.interhomeService',
                    ),
                    searchFilter.interhomeService,
                    'interhomeService',
                  )}
                  {formatSelectedValue(
                    t(
                      'searchBookableAccommodations:selectedFilters.nearbySearchRadius',
                    ),
                    searchFilter.nearbySearchRadius,
                    'nearbySearchRadius',
                  )}

                  {formatSelectedEquipments(
                    t(
                      'searchBookableAccommodations:selectedFilters.facilities',
                    ),
                    searchFilter.booleanAttributes,
                    searchFilter.multiSelectAttributes,
                  )}

                  {formatSelectedDistances(
                    t(
                      'searchBookableAccommodations:selectedFilters.distancesMin',
                    ),
                    searchFilter.minimumNumericAttributes,
                  )}

                  {formatSelectedDistances(
                    t(
                      'searchBookableAccommodations:selectedFilters.distancesMax',
                    ),
                    searchFilter.maximumNumericAttributes,
                  )}
                </div>

                <Button
                  onClick={doClear}
                  variant="outline-blue"
                  size="sm"
                  className="ms-auto"
                >
                  {t('common:buttons.emptySearchFilters')}
                </Button>
              </div>
            </>
          )}
        </AccommodationAutoCompleteSearchBar>
        {basketContainer != null &&
          basketContainer.bookingResponses &&
          basketContainer.bookingResponses.length > 0 && (
            <div
              className="d-flex mb-24 align-items-center"
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 999,
                background: 'var(--bs-body-bg, #fff)',
                paddingTop: '8px',
                paddingBottom: '8px',
              }}
            >
              <h2 className="h5 flex-grow-1 me-16">
                {t('searchBookableAccommodations:header.breadcrumb')}
              </h2>
              <Button variant="danger" onClick={() => goToBasket()}>
                {t('searchBookableAccommodations:booking.goToBasket')}(
                {basketContainer.bookingResponses.length})
              </Button>
            </div>
          )}
        {loadingStatus === LOADING_STATUS.IDLE &&
          searchResultItems &&
          searchResultItems.length > 0 && (
            <>
              <div className="pb-24">
                {searchResultItemsPagination.totalCount}{' '}
                {t('searchBookableAccommodations:results.accommodations')}
              </div>
              <Card className="border shadow-none">
                <div className="table-responsive-lg">
                  <Table hover className="table-data">
                    <thead>
                      <tr>
                        <th>&nbsp;</th>
                        <th
                          onClick={() => handleSort('AccommodationNumber')}
                          style={{ cursor: 'pointer' }}
                        >
                          {t(
                            'searchBookableAccommodations:results.headers.accommodationNumber',
                          )}{' '}
                          <span style={{ fontSize: '1.5em' }}>
                            {getSortIndicator('AccommodationNumber')}
                          </span>
                        </th>
                        <th
                          onClick={() => handleSort('AccommodationName')}
                          style={{ cursor: 'pointer' }}
                        >
                          {t(
                            'searchBookableAccommodations:results.headers.accommodationName',
                          )}{' '}
                          <span style={{ fontSize: '1.5em' }}>
                            {getSortIndicator('AccommodationName')}
                          </span>
                        </th>
                        <th>
                          {t(
                            'searchBookableAccommodations:results.headers.bookingDates',
                          )}{' '}
                          <span style={{ fontSize: '1.5em' }} />
                        </th>
                        <th
                          onClick={() => handleSort('Price')}
                          style={{ cursor: 'pointer' }}
                        >
                          {t(
                            'searchBookableAccommodations:results.headers.price',
                          )}{' '}
                          <span style={{ fontSize: '1.5em' }}>
                            {getSortIndicator('Price')}
                          </span>
                        </th>
                        {!isTravelAgencyOrDomainUser && (
                          <th
                            onClick={() => handleSort('Owner')}
                            style={{ cursor: 'pointer' }}
                          >
                            {t(
                              'searchBookableAccommodations:results.headers.owner',
                            )}{' '}
                            <span style={{ fontSize: '1.5em' }}>
                              {getSortIndicator('Owner')}
                            </span>
                          </th>
                        )}
                        <th />
                      </tr>
                    </thead>
                    <tbody className="border-bottom">
                      {searchResultItemsList}
                    </tbody>
                  </Table>
                  <PaginationComponent
                    OnPageClick={HandlePaginationClick}
                    PaginationData={searchResultItemsPagination}
                  />
                </div>
              </Card>
            </>
          )}
        {loadingStatus === LOADING_STATUS.IDLE &&
          searchResultItems &&
          searchResultItems.length === 0 &&
          submitted && (
            <p>{t('searchBookableAccommodations:results.noResults')}</p>
          )}
      </Container>
      {isTravelAgencyOrDomainUser && agentViewableAccommodation !== '' && (
        <Modal
          onHide={() => setIsAgentAccommodationModalOpen(false)}
          show={isAgentAccommodationModalOpen}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title />
          </Modal.Header>
          <Modal.Body>
            <AgentAccommodationView
              AccommodationSlug={agentViewableAccommodation}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}

export default SearchBookableAccommodations;
